import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Paywall from 'features/paywall/Wrapper/Widget';
import { SideWidgetAnalytics as WIDGET } from 'features/paywall/analytics';
import useRunningTrade from 'global/Datafeed/useRunningTrade';
import useDatafeedSocket from 'global/Datafeed/useDatafeedSocket';
import PrimarySpinner from 'global/Loading/PrimarySpinner';
import { useRunningTradeStore } from 'global/Datafeed/store/runningTrade';
import FailedLoad from './Failed';
import Header from './Header';
import RunningTradeTable from './RunningTradeTable';
import { Box } from './styled';
import useRunningTradeInit from './useRunningTradeInit';
import RunningTradeInitTable from './RunningTradeInitTable';
import ActionControl from './ActionControl';
import { useFilterStore } from './store';
import { mapCustomFilter } from './utils';
import useRunningTradeCustomFilter from './useRunningTradeCustomFilter';
import { StockFilterTypeEnum } from './types';

const tableIdentifierClassName = 'table-scroller';

interface RunningTradeProps {
  runningTradeKey?: RunningTradeKey;
  className?: string;
  minDataLength?: number;
  isPro: boolean;
  fullWidth?: boolean;
  hideHeader?: boolean;
  hideCode?: boolean;
  hideStockFilter?: boolean;
  playAlignRight?: boolean;
  isVolumeColor?: boolean;
  allowInitialData?: boolean;
}

function RunningTrade({
  runningTradeKey = 'widget',
  className,
  minDataLength = 10,
  isPro,
  hideHeader,
  hideCode = false,
  hideStockFilter = false,
  playAlignRight = false,
  fullWidth,
  isVolumeColor = false,
  allowInitialData = false,
}: RunningTradeProps) {
  const [isOpenMarket, setIsOpenMarket] = useState(false);

  // performance issue in BE, so we need to use V1
  // will be updated if BE fixing the V2 version
  const runningTradeVersion = 'V2';

  const filter = useFilterStore((s) => s.filter);
  const stocksFilter = useFilterStore((s) => s.stocksFilter);
  const isPaused = useRunningTradeStore((s) => s.runningTrade.widget.isPaused);
  const onApplyStockFilter = useFilterStore((s) => s.onApplyStockFilter);

  const combinedCustomFilter = useMemo(
    () =>
      mapCustomFilter(
        { ...filter, ...stocksFilter },
        allowInitialData ? false : isOpenMarket,
      ),
    [filter, stocksFilter, isOpenMarket],
  );

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { runningTradeFilter } = useRunningTradeCustomFilter(true);

  const {
    isLoading,
    runningTrade,
    setInstanceActive,
    setInstanceFilter,
    setDebounceInstanceFilter,
  } = useRunningTrade(runningTradeKey, runningTradeFilter);
  const { error: datafeedError } = useDatafeedSocket();

  const {
    runningTradeRes,
    runningTrades,
    isInitiating,
    isLoadingMore,
    hasMore,
    loadMore,
    params,
    setParams,
  } = useRunningTradeInit({
    version: runningTradeVersion,
    defaultParams: {
      sort: 'desc',
      order_by: 'ORDER_BY_TIME',
      limit: 50,
      ...combinedCustomFilter,
    },
    onSuccess: (data) => {
      setInstanceActive(data.is_open_market);
      setIsOpenMarket(data.is_open_market);

      if (
        !data.is_open_market &&
        stocksFilter.type === StockFilterTypeEnum.Custom &&
        stocksFilter?.selected?.length > 1
      ) {
        onApplyStockFilter({
          type: StockFilterTypeEnum.AllStock,
          selected: [],
        });
      }
    },
  });

  useEffect(() => {
    if (allowInitialData || (!isInitiating && !isOpenMarket)) {
      setParams((s) => ({
        sort: s.sort,
        limit: s.limit,
        order_by: s.order_by,
        trade_number: undefined,
        ...mapCustomFilter(
          { ...filter, ...stocksFilter },
          allowInitialData ? false : isOpenMarket,
        ),
      }));
    }
  }, [filter, stocksFilter, allowInitialData]);

  const handleReloadClick = () => {
    window.location.reload();
  };

  const scrollToTop = useCallback(() => {
    // Reset ant table body scroll position back to top
    if (wrapperRef) {
      const scrollableEl = wrapperRef.current.querySelector(
        `.${tableIdentifierClassName}`,
      );
      if (!scrollableEl) return;

      scrollableEl.scrollTo({ top: 0 });
    }
  }, []);

  const handleSetSocketFilter = useCallback((symbol: string) => {
    setDebounceInstanceFilter(symbol || '*');
  }, []);

  const handleSetAPIFilter = useCallback(
    (symbol: string) => {
      setParams((s) => ({
        ...s,
        symbol,
        trade_number: undefined,
      }));

      scrollToTop();
      setInstanceFilter(symbol);
    },
    [scrollToTop],
  );

  const handleTableSort = useCallback(
    (field?: GetInitRunningTradeOrderBy, order?: GetInitRunningTradeSort) => {
      setParams((s) => ({
        ...s,
        sort: order,
        order_by: field,
        trade_number: undefined,
      }));

      scrollToTop();
    },
    [],
  );

  useEffect(() => {
    setInstanceFilter(runningTrade.filter || '*');

    if (runningTrade.filter !== '' && runningTrade.filter !== '*') {
      handleSetSocketFilter(runningTrade.filter);
      handleSetAPIFilter(runningTrade.filter);
    }
  }, [runningTrade.filter]);

  if (
    !isInitiating &&
    runningTradeRes &&
    runningTradeRes.is_open_market &&
    datafeedError
  ) {
    return <FailedLoad onReloadClick={handleReloadClick} />;
  }

  return (
    <Box id="running-trade" className={className} ref={wrapperRef}>
      {!hideHeader && <Header>Running Trade</Header>}
      {isPro ? (
        <div
          style={{
            overflow: 'hidden',
            height: 'calc(100vh - 42px)',
          }}
        >
          {isInitiating ? (
            <PrimarySpinner />
          ) : (
            <>
              <ActionControl
                isMarketOpen={runningTradeRes?.is_open_market}
                hideStockFilter={hideStockFilter}
                playAlignRight={playAlignRight}
              />
              {runningTradeRes && runningTradeRes.is_open_market ? (
                <RunningTradeTable
                  initialData={
                    allowInitialData ? runningTradeRes.running_trade : []
                  }
                  minDataLength={minDataLength}
                  data={runningTrade.data}
                  loading={isLoading}
                  showAction
                  height="calc(100vh - 133px)"
                  hideCode={hideCode}
                  isPaused={isPaused}
                  isCodeBold
                  isVolumeColor={isVolumeColor}
                />
              ) : (
                <RunningTradeInitTable
                  id={tableIdentifierClassName}
                  data={runningTrades}
                  showCode={!hideCode}
                  showAction
                  hasMore={hasMore}
                  loadMore={loadMore}
                  isLoadingMore={isLoadingMore}
                  height="calc(100vh - 102px)"
                  handleTableSort={handleTableSort}
                  defaultSort={{ direction: params.sort, key: params.order_by }}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <Paywall height="90vh" source={WIDGET.RUNNING_TRADE} />
      )}
    </Box>
  );
}

export default RunningTrade;
